import { InjectionToken } from '@angular/core';

/**
 * The app's OWN IdP access token (Auth0 or Entra), resolved silently at
 * capture time. `null` means "none right now" and the recorder falls back to
 * the shared ingest key. Must never trigger interactive login.
 */
export type RynReplayBearerProvider = () => Promise<string | null>;

/**
 * Opt-in switch for signed ingest tokens (#30115 PR 2).
 *
 * An app that provides this gets token mode: the recorder presents this
 * bearer to `POST {ingest base}/ingest-token`, receives a 10-minute token bound
 * to one replay session and one Origin, and sends that as
 * `Authorization: Bearer` instead of `X-Ryn-Replay-Key`. An app that does not
 * provide it keeps today's key-only behaviour and never calls the mint.
 *
 * Inert until the server is keyed: an unkeyed mint answers 503, the client
 * backs off, and every batch still goes out on the key.
 */
export const RYN_REPLAY_BEARER_PROVIDER =
  new InjectionToken<RynReplayBearerProvider>('RYN_REPLAY_BEARER_PROVIDER');

/**
 * Wrap an app's silent token acquisition so it can never throw into the
 * recorder: a rejection, a synchronous throw, or an empty token all resolve
 * `null`. Apps build their provider through this so the "catch to null" rule
 * lives in one place instead of per app.
 */
export function guardRynReplayBearerProvider(
  acquire: () => Promise<string | null | undefined>
): RynReplayBearerProvider {
  return async () => {
    try {
      const token = await acquire();
      return token ? token : null;
    } catch {
      return null;
    }
  };
}
